import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ArchivedTasksList } from './archivedTasksList';
import { UnarchivePreview } from './unarchivePreview';
import { useArchivingProgressHub } from '../hooks/useArchivingProgressHub';
import { useIsUnarchivingJobActive, useFetchUndismissedUnarchivingJobResults } from './hooks';
import { EUnarchivePreviewTab } from './constants';
import { useRMDispatch } from 'redux/hooks';
import {
  fetchActiveUnarchiveJobResultsThunk,
  fetchArchivedExperimentsThunk,
  fetchArchivedTasksThunk,
  fetchUndismissedUnarchivingJobResultsThunk,
} from 'redux/thunks/unarchiving';
import { JobTypeDTO } from 'components/main/Setup/Imaging/ImageManagementTask/constants';
import {
  ARCHIVED_FILES_SECTION_PARAM,
  ARCHIVED_FILES_SECTION_VALUE,
  ARCHIVING_RESULTS_JOB_ID_PARAM,
  ARCHIVING_RESULTS_TAB_PARAM,
  ARCHIVING_RESULTS_TAB_VALUE,
} from '../constants';

export const ArchivedFiles = () => {
  const [selectedTaskIds, setSelectedTaskIds] = useState<number[]>([]);
  const dispatch = useRMDispatch();
  const handleUnarchiveSuccess = useCallback(() => setSelectedTaskIds([]), []);

  const { search, key: locationKey } = useLocation();
  const searchParams = new URLSearchParams(search);
  const isUnarchiveSection =
    searchParams.get(ARCHIVED_FILES_SECTION_PARAM) === ARCHIVED_FILES_SECTION_VALUE;
  const requestedTab = isUnarchiveSection
    ? searchParams.get(ARCHIVING_RESULTS_TAB_PARAM)
    : null;
  const requestedJobId = isUnarchiveSection
    ? searchParams.get(ARCHIVING_RESULTS_JOB_ID_PARAM)
    : null;
  useFetchUndismissedUnarchivingJobResults(requestedJobId);

  const [selectedTab, setSelectedTab] = useState(() =>
    requestedTab === ARCHIVING_RESULTS_TAB_VALUE
      ? EUnarchivePreviewTab.Results
      : EUnarchivePreviewTab.Preview,
  );

  useArchivingProgressHub([JobTypeDTO.Unarchive]);
  const isJobActive = useIsUnarchivingJobActive();
  const wasJobActiveRef = useRef(false);

  // Surface progress by moving to the Results tab while an unarchive job is running.
  useEffect(() => {
    if (isJobActive) {
      setSelectedTab(EUnarchivePreviewTab.Results);
    }
  }, [isJobActive]);

  // Once a SignalR-driven unarchive job finishes, re-fetch the folder list: a folder that was
  // fully unarchived is now empty and should no longer appear.
  useEffect(() => {
    if (wasJobActiveRef.current && !isJobActive) {
      dispatch(fetchArchivedTasksThunk());
      if (requestedJobId) {
        dispatch(fetchActiveUnarchiveJobResultsThunk(requestedJobId));
      } else {
        dispatch(fetchUndismissedUnarchivingJobResultsThunk());
      }
      // The Preview tab's own experiments were cleared when the unarchive job was
      // submitted; re-fetch them for the still-selected folder(s) so the Preview tab
      // shows the remaining archived experiments instead of staying empty.
      if (selectedTaskIds.length > 0) {
        dispatch(fetchArchivedExperimentsThunk(selectedTaskIds));
      }
    }
    wasJobActiveRef.current = isJobActive;
  }, [isJobActive, dispatch, selectedTaskIds, requestedJobId]);

  useEffect(() => {
    if (requestedJobId) {
      dispatch(fetchActiveUnarchiveJobResultsThunk(requestedJobId));
      setSelectedTab(EUnarchivePreviewTab.Results);
    }
  }, [requestedJobId, locationKey, dispatch]);

  return (
    <div className="tab-content-height w-full flex flex-row">
      <div className="basis-[25%] w-[25%] border-solid border-0 border-r-2 border-[#E0E0E0] flex flex-col p-2 gap-2">
        <ArchivedTasksList
          selectedTaskIds={selectedTaskIds}
          setSelectedTaskIds={setSelectedTaskIds}
          disabled={selectedTab === EUnarchivePreviewTab.Results}
        />
      </div>
      <div className="basis-[75%] w-[75%] flex h-full">
        <UnarchivePreview
          selectedTaskIds={selectedTaskIds}
          selectedTab={selectedTab}
          onSelectedTabChange={setSelectedTab}
          resultsJobId={requestedJobId}
          onUnarchiveSuccess={handleUnarchiveSuccess}
        />
      </div>
    </div>
  );
};
